import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { queryClient } from '../lib/queryClient';
import { useSelectedCompanyStore } from '../stores/selectedCompany.store';
import { useDocumentsDeepLink } from '../hooks/useDocumentsDeepLink';
import { useUserCompaniesStore } from '../stores/userCompanies.store';
import {
  useListNodes,
  useGetDocumentPresignedUrl,
  useValidateDocument,
  useListFolderOldVersions,
} from '../services/documents.service';
import { DocumentsFolderView } from '../components/documents/DocumentsFolderView';
import { DocumentsRootView } from '../components/documents/DocumentsRootView';
import {
  CreateFolderModal,
  type CreateFolderModalRef,
} from '../components/documents/CreateFolderModal';
import { RenameNodeModal, type RenameNodeModalRef } from '../components/documents/RenameNodeModal';
import {
  UploadDocumentModal,
  type UploadDocumentModalRef,
} from '../components/documents/UploadDocumentModal';
import {
  UploadNewVersionModal,
  type UploadNewVersionModalRef,
} from '../components/documents/UploadNewVersionModal';
import {
  DiffuseDocumentModal,
  type DiffuseDocumentModalRef,
} from '../components/documents/DiffuseDocumentModal';
import {
  ResendDocumentModal,
  type ResendDocumentModalRef,
} from '../components/documents/ResendDocumentModal';
import {
  SendForValidationModal,
  type SendForValidationModalRef,
} from '../components/documents/SendForValidationModal';
import {
  RejectDocumentModal,
  type RejectDocumentModalRef,
} from '../components/documents/RejectDocumentModal';
import {
  DeleteDocumentModal,
  type DeleteDocumentModalRef,
} from '../components/documents/DeleteDocumentModal';
import {
  ArchiveDocumentModal,
  type ArchiveDocumentModalRef,
} from '../components/documents/ArchiveDocumentModal';
import {
  DocumentReadersModal,
  type DocumentReadersModalRef,
} from '../components/documents/DocumentReadersModal';
import { DocumentNodeType, DocumentStatus, isRoleAtLeast, UserCompanyRole } from '@monorepo/shared';
import type { DocumentNodeWithDetailsDto } from '@monorepo/shared';
import { SubscriptionPaywall } from '../components/subscription/SubscriptionPaywall';
import { useIsCompanyPaywalled } from '../components/subscription/useIsCompanyPaywalled';

export const DocumentsPage = () => {
  const { t } = useTranslation();
  const { folderId } = useParams<{ folderId?: string }>();
  const navigate = useNavigate();
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);
  const companies = useUserCompaniesStore((state) => state.companies);
  const currentUserRole = companies.find((c) => c.id === selectedCompanyId)?.role;
  const { isPaywalled, companyName } = useIsCompanyPaywalled();

  const currentParentId = folderId ?? null;
  const isInsideFolder = currentParentId !== null;
  const [searchQuery, setSearchQuery] = useState('');

  const createFolderModalRef = useRef<CreateFolderModalRef>(null);
  const renameNodeModalRef = useRef<RenameNodeModalRef>(null);
  const uploadDocumentModalRef = useRef<UploadDocumentModalRef>(null);
  const diffuseDocumentModalRef = useRef<DiffuseDocumentModalRef>(null);
  const resendDocumentModalRef = useRef<ResendDocumentModalRef>(null);
  const sendForValidationModalRef = useRef<SendForValidationModalRef>(null);
  const rejectDocumentModalRef = useRef<RejectDocumentModalRef>(null);
  const deleteDocumentModalRef = useRef<DeleteDocumentModalRef>(null);
  const uploadNewVersionModalRef = useRef<UploadNewVersionModalRef>(null);
  const archiveDocumentModalRef = useRef<ArchiveDocumentModalRef>(null);
  const documentReadersModalRef = useRef<DocumentReadersModalRef>(null);

  const validateDocumentMutation = useValidateDocument(selectedCompanyId || '');

  const { data, isLoading, isFetching } = useListNodes(
    selectedCompanyId || '',
    currentParentId ?? undefined,
    { enabled: !isPaywalled },
  );

  const { highlightedNodeId } = useDocumentsDeepLink({
    nodes: data?.nodes,
    isFetching,
    isInsideFolder,
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries();
    toast.success(t('documents.refresh_success'));
  };

  const { data: oldVersionsData } = useListFolderOldVersions(
    selectedCompanyId || '',
    currentParentId ?? undefined,
    { enabled: !isPaywalled },
  );

  const getPresignedUrlMutation = useGetDocumentPresignedUrl(selectedCompanyId || '');

  const loadingDocumentId = validateDocumentMutation.isPending
    ? (validateDocumentMutation.variables?.documentId ?? null)
    : null;

  const canUploadDocx = currentUserRole
    ? isRoleAtLeast(currentUserRole, UserCompanyRole.SUPER_USER)
    : false;

  const handleOpenFolder = (nodeId: string) => {
    setSearchQuery('');
    navigate(`/documents/${nodeId}`);
  };

  const handleNavigate = (nodeId: string | null) => {
    setSearchQuery('');
    if (nodeId) {
      navigate(`/documents/${nodeId}`);
    } else {
      navigate('/documents');
    }
  };

  const handleAddDocument = () => {
    uploadDocumentModalRef.current?.open(currentParentId);
  };

  const handleAddFolder = () => {
    createFolderModalRef.current?.open(currentParentId);
  };

  const handleOpenDocument = async (nodeId: string) => {
    try {
      const result = await getPresignedUrlMutation.mutateAsync(nodeId);
      window.open(result.url, '_blank');
    } catch (error) {
      console.error('Error opening document:', error);
    }
  };

  const handleRename = (node: DocumentNodeWithDetailsDto) => {
    renameNodeModalRef.current?.open(node);
  };

  const handleDeleteClick = (node: DocumentNodeWithDetailsDto) => {
    deleteDocumentModalRef.current?.open(node.id, node.name);
  };

  const handleDiffuse = (doc: DocumentNodeWithDetailsDto) => {
    if (doc.documentId) {
      diffuseDocumentModalRef.current?.open(doc.documentId, doc.name);
    }
  };

  const handleResend = (doc: DocumentNodeWithDetailsDto) => {
    if (doc.documentId) {
      resendDocumentModalRef.current?.open(doc.documentId);
    }
  };

  const handleSendForValidation = (doc: DocumentNodeWithDetailsDto) => {
    if (doc.documentId) {
      sendForValidationModalRef.current?.open(doc.documentId, doc.name);
    }
  };

  const handleValidate = (doc: DocumentNodeWithDetailsDto) => {
    if (doc.documentId) {
      validateDocumentMutation.mutate({ documentId: doc.documentId });
    }
  };

  const handleReject = (doc: DocumentNodeWithDetailsDto) => {
    if (doc.documentId) {
      rejectDocumentModalRef.current?.open(doc.documentId, doc.name);
    }
  };

  const handleUploadNewVersion = (doc: DocumentNodeWithDetailsDto) => {
    if (doc.documentId) {
      uploadNewVersionModalRef.current?.open(doc.documentId, doc.name);
    }
  };

  const handleArchive = (doc: DocumentNodeWithDetailsDto) => {
    if (doc.documentId) {
      archiveDocumentModalRef.current?.open(doc.documentId, doc.name);
    }
  };

  const handleShowReaders = (doc: DocumentNodeWithDetailsDto) => {
    if (doc.documentId) {
      documentReadersModalRef.current?.open(doc.documentId, doc.name);
    }
  };

  const filteredNodes =
    data?.nodes.filter((node) => node.name.toLowerCase().includes(searchQuery.toLowerCase())) ?? [];

  const folders = filteredNodes.filter((n) => n.type === DocumentNodeType.FOLDER);
  const allDocuments = filteredNodes.filter((n) => n.type === DocumentNodeType.DOCUMENT);
  const documents = allDocuments.filter((n) => n.status !== DocumentStatus.ARCHIVED);
  const archivedDocuments = allDocuments.filter((n) => n.status === DocumentStatus.ARCHIVED);

  if (!selectedCompanyId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">{t('common.loading')}</p>
      </div>
    );
  }

  if (isPaywalled) {
    return <SubscriptionPaywall companyName={companyName} />;
  }

  return (
    <>
      {isInsideFolder ? (
        <DocumentsFolderView
          companyId={selectedCompanyId}
          currentParentId={currentParentId}
          breadcrumb={data?.breadcrumb ?? []}
          folders={folders}
          documents={documents}
          archivedDocuments={archivedDocuments}
          oldVersionsCount={oldVersionsData?.totalCount ?? 0}
          isLoading={isLoading}
          currentUserRole={currentUserRole}
          onNavigate={handleNavigate}
          onRefresh={handleRefresh}
          onAddFolder={handleAddFolder}
          onAddDocument={handleAddDocument}
          onOpenFolder={handleOpenFolder}
          onOpenDocument={handleOpenDocument}
          onRename={handleRename}
          onDelete={handleDeleteClick}
          onDiffuse={handleDiffuse}
          onResend={handleResend}
          onSendForValidation={handleSendForValidation}
          onValidate={handleValidate}
          onReject={handleReject}
          onUploadNewVersion={handleUploadNewVersion}
          onArchive={handleArchive}
          onShowReaders={handleShowReaders}
          loadingDocumentId={loadingDocumentId}
          highlightedNodeId={highlightedNodeId}
        />
      ) : (
        <DocumentsRootView
          companyId={selectedCompanyId}
          folders={folders}
          totalCount={data?.nodes.length ?? 0}
          isLoading={isLoading}
          currentUserRole={currentUserRole}
          isAtRoot={currentParentId === null}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
          onAddFolder={handleAddFolder}
          onOpenFolder={handleOpenFolder}
          onRename={handleRename}
          onDelete={handleDeleteClick}
        />
      )}

      <CreateFolderModal ref={createFolderModalRef} companyId={selectedCompanyId} />
      <RenameNodeModal ref={renameNodeModalRef} companyId={selectedCompanyId} />
      <UploadDocumentModal
        ref={uploadDocumentModalRef}
        companyId={selectedCompanyId}
        allowDocx={canUploadDocx}
      />
      <DiffuseDocumentModal
        ref={diffuseDocumentModalRef}
        companyId={selectedCompanyId}
        currentUserRole={currentUserRole}
      />
      <ResendDocumentModal ref={resendDocumentModalRef} companyId={selectedCompanyId} />
      <SendForValidationModal ref={sendForValidationModalRef} companyId={selectedCompanyId} />
      <RejectDocumentModal ref={rejectDocumentModalRef} companyId={selectedCompanyId} />
      <DeleteDocumentModal ref={deleteDocumentModalRef} companyId={selectedCompanyId} />
      <DocumentReadersModal ref={documentReadersModalRef} companyId={selectedCompanyId} />
      <UploadNewVersionModal ref={uploadNewVersionModalRef} companyId={selectedCompanyId} />
      <ArchiveDocumentModal ref={archiveDocumentModalRef} companyId={selectedCompanyId} />
    </>
  );
};
